/* SIZESOFTX
=========================================================================================
* Descripción: Resumen de indicadores del panel de soporte
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DevicesIcon from "@mui/icons-material/Devices";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { apiNotificarVencimiento, apiResumen, apiValidacionLicencias } from "../settings/supportApi";

const StatCard = ({ title, value, icon }) => (
  <Card>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ color: "#1976d2", display: "flex" }}>{icon}</Box>
      <Box>
        <Typography variant="h4">{value}</Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const ResumenPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [validacionOpen, setValidacionOpen] = useState(false);
  const [licencias, setLicencias] = useState(null);
  const [licenciasLoading, setLicenciasLoading] = useState(false);
  const [licenciasError, setLicenciasError] = useState("");
  const [licenciasOk, setLicenciasOk] = useState("");
  const [notificando, setNotificando] = useState(null);
  const [soloCorreo, setSoloCorreo] = useState(true);

  useEffect(() => {
    apiResumen()
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  const fmt = (v) => (v ? new Date(v).toLocaleString("es-MX") : "—");
  const fmtDias = (d) => (d === null || d === undefined ? "—" : d < 0 ? `${d} (vencida)` : `${d} días`);

  const cargarValidacion = async (dias) => {
    setLicenciasLoading(true);
    setLicenciasError("");
    setLicenciasOk("");
    try {
      setLicencias(await apiValidacionLicencias(dias));
    } catch (err) {
      setLicenciasError(err.message);
    } finally {
      setLicenciasLoading(false);
    }
  };

  const abrirValidacion = () => {
    setValidacionOpen(true);
    cargarValidacion(15);
  };

  const notificar = async (dias) => {
    setNotificando(dias);
    setLicenciasError("");
    setLicenciasOk("");
    try {
      const res = await apiNotificarVencimiento({ diasAnticipacion: dias });
      setLicenciasOk(res?.mensaje || "Notificaciones enviadas.");
      await cargarValidacion(dias);
    } catch (err) {
      setLicenciasError(err.message);
    } finally {
      setNotificando(null);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );

  const items = [
    { title: "Tenants totales", value: data.totalTenants, icon: <StorefrontIcon /> },
    { title: "Tenants activos", value: data.tenantsActivos, icon: <StorefrontIcon /> },
    { title: "Tenants suspendidos", value: data.tenantsSuspedidos, icon: <StorefrontIcon /> },
    { title: "Licencias activas", value: data.licenciasActivas, icon: <VerifiedUserIcon /> },
    { title: "Licencias expiradas", value: data.licenciasExpiradas, icon: <VerifiedUserIcon /> },
    { title: "Equipos en uso", value: data.equiposEnUso, icon: <DevicesIcon /> },
    { title: "Equipos pendientes", value: data.equiposPendientes, icon: <DevicesIcon /> },
    { title: "Solicitudes acceso móvil", value: data.solicitudesAccesoPendientes, icon: <DevicesIcon /> },
    { title: "Intentos fallidos", value: data.intentosFallidos, icon: <MailOutlineIcon /> },
    { title: "Mensajes WhatsApp", value: data.mensajesWhatsApp, icon: <MailOutlineIcon /> },
    { title: "Mensajes correo", value: data.mensajesCorreo, icon: <MailOutlineIcon /> },
    { title: "Saldos bajos", value: data.saldosBajos, icon: <MailOutlineIcon /> },
    { title: "Próximos vencimientos (15d)", value: data.proximosVencimientos, icon: <VerifiedUserIcon /> },
  ];

  const visibles = (licencias?.licencias || []).filter(
    (l) => !soloCorreo || l.emailLicencia || l.emailContacto
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Resumen de soporte</Typography>
        <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={abrirValidacion}>
          Validar licencias
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {items.map((it) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={it.title}>
            <StatCard title={it.title} value={it.value} icon={it.icon} />
          </Grid>
        ))}
      </Grid>

      <Dialog open={validacionOpen} onClose={() => setValidacionOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Validación de licencias</DialogTitle>
        <DialogContent dividers>
          {licenciasError && <Alert severity="error" sx={{ mb: 2 }}>{licenciasError}</Alert>}
          {licenciasOk && <Alert severity="success" sx={{ mb: 2 }}>{licenciasOk}</Alert>}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body1">
              Antes de{" "}
              <strong>
                {licencias ? licencias.diasAnticipacion : "—"} días
              </strong>
            </Typography>
            <Button size="small" variant="outlined" disabled={licenciasLoading} onClick={() => cargarValidacion(5)}>
              Ver 5 días
            </Button>
            <Button size="small" variant="outlined" disabled={licenciasLoading} onClick={() => cargarValidacion(15)}>
              Ver 15 días
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2">Solo con correo</Typography>
            <Checkbox size="small" checked={soloCorreo} onChange={(e) => setSoloCorreo(e.target.checked)} />
          </Stack>

          {licencias && !licenciasLoading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total <strong>{licencias.total}</strong> · Por vencer <strong>{licencias.porVencer}</strong> ·
                Expiradas <strong>{licencias.expiradas}</strong> · Sin correo <strong>{licencias.sinCorreo}</strong>
              </Typography>
            </Box>
          )}

          {licenciasLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {licencias && !licenciasLoading && (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<MailOutlineIcon />}
                disabled={notificando !== null}
                onClick={() => notificar(5)}
              >
                {notificando === 5 ? "Enviando…" : "Notificar (5 días)"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<MailOutlineIcon />}
                disabled={notificando !== null}
                onClick={() => notificar(15)}
              >
                {notificando === 15 ? "Enviando…" : "Notificar (15 días)"}
              </Button>
            </Stack>
          )}

          {licencias && !licenciasLoading && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Clave</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Licencia</TableCell>
                    <TableCell>Estatus</TableCell>
                    <TableCell>Vence</TableCell>
                    <TableCell>Días rest.</TableCell>
                    <TableCell>Correo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Sin licencias con el filtro actual.
                      </TableCell>
                    </TableRow>
                  )}
                  {visibles.map((l) => (
                    <TableRow key={l.tenantActivationLicenseId} sx={l.expirada ? { bgcolor: "#fdecea" } : l.activa ? { bgcolor: "#e8f5e9" } : { bgcolor: "#fff8e1" }}>
                      <TableCell>{l.tenantKey}</TableCell>
                      <TableCell>{l.nombreComercial || l.razonSocial}</TableCell>
                      <TableCell>{l.licenseCode}</TableCell>
                      <TableCell>
                        {l.estatus} {l.expirada && <strong>(vencida)</strong>}
                      </TableCell>
                      <TableCell>{fmt(l.expiresAtUtc)}</TableCell>
                      <TableCell>{fmtDias(l.diasRestantes)}</TableCell>
                      <TableCell>{l.emailLicencia || l.emailContacto || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidacionOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResumenPage;
